/**
 * Преобразует строковый literal type из snake_case в camelCase.
 */
type SnakeToCamel<T extends string> = T extends `${infer Head}_${infer Tail}`
    ? `${Head}${Capitalize<SnakeToCamel<Tail>>}`
    : T;

/**
 * Рекурсивно преобразует ключи объекта из snake_case в camelCase на уровне типов.
 *
 * Массивы и кортежи преобразуются поэлементно, а Date, File, Blob и FormData
 * остаются без изменений.
 */
export type Camelize<T> = T extends readonly [infer First, ...infer Rest]
    ? readonly [Camelize<First>, ...Camelize<Rest>]
    : T extends readonly (infer Item)[]
    ? Camelize<Item>[]
    : T extends Date | File | Blob | FormData
    ? T
    : T extends object
    ? {
          [Key in keyof T as Key extends string ? SnakeToCamel<Key> : Key]: Camelize<T[Key]>;
      }
    : T;

/**
 * Преобразует строку из snake_case в camelCase.
 */
const snakeToCamel = (value: string) =>
    value.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());

/**
 * Проверяет, что значение является простым объектом.
 *
 * Используется, чтобы не обходить специальные объекты вроде Date, File,
 * Blob, FormData и экземпляры классов.
 */
const isPlainObject = (value: unknown): value is Record<PropertyKey, unknown> => {
    if (value === null || typeof value !== 'object') {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
};

/**
 * Рекурсивно преобразует ключи plain object из snake_case в camelCase.
 *
 * Массивы обрабатываются поэлементно. Примитивы, null и специальные объекты
 * возвращаются без изменений.
 */
export const camelizeKeys = <T>(input: T): Camelize<T> => {
    if (Array.isArray(input)) {
        return input.map((item) => camelizeKeys(item)) as Camelize<T>;
    }

    if (isPlainObject(input)) {
        return Object.entries(input).reduce<Record<string, unknown>>((acc, [key, value]) => {
            acc[snakeToCamel(key)] = camelizeKeys(value);

            return acc;
        }, {}) as Camelize<T>;
    }

    return input as Camelize<T>;
};
